import { useState, useRef, useEffect } from 'react'
import { getDateRangePresets, formatDisplayDate } from '../lib/date-utils'

interface DateRangePickerProps {
  value: { start_date: string; end_date: string }
  onChange: (range: { start_date: string; end_date: string }) => void
}

export function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const presets = getDateRangePresets()

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm bg-background border border-input rounded-lg hover:bg-secondary/50 transition-colors"
      >
        <svg
          className="w-4 h-4 text-muted-foreground"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="text-foreground">
          {formatDisplayDate(value.start_date)} -{' '}
          {formatDisplayDate(value.end_date)}
        </span>
        <svg
          className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-20">
          <div className="p-2">
            <p className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Presets
            </p>
            {presets.map((preset) => (
              <button
                key={preset.label}
                onClick={() => {
                  onChange(preset.getDates())
                  setIsOpen(false)
                }}
                className="w-full text-left px-3 py-2 text-sm rounded hover:bg-secondary/50 transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="border-t border-border p-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Custom Range
            </p>
            <div className="space-y-2">
              <div>
                <label className="text-xs text-muted-foreground">Start</label>
                <input
                  type="date"
                  value={value.start_date}
                  onChange={(e) =>
                    onChange({ ...value, start_date: e.target.value })
                  }
                  className="w-full mt-1 px-2 py-1 text-sm border border-input rounded bg-background"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">End</label>
                <input
                  type="date"
                  value={value.end_date}
                  onChange={(e) =>
                    onChange({ ...value, end_date: e.target.value })
                  }
                  className="w-full mt-1 px-2 py-1 text-sm border border-input rounded bg-background"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
